/* eslint-disable eqeqeq */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * FAN PRINT SHEET — React port of `fan.selector.print.tmpl` (749 lines).
 *
 * Legacy `print()` (fan.selector.js:8121-8204) + `correctPrintStyles()`
 * (:8313-8383) compile that Handlebars template, drop the HTML into a
 * `div.print-content` appended to `<body>`, draw the fan curve into the sheet's
 * OWN `#curves` canvas, set `document.title` and call `window.print()` 200 ms
 * later.
 *
 * That sheet is the whole point of Print, because `common.css`'s print block
 * hides the app entirely —
 *   `@media print { body > *:not([class*=print]) { display: none !important } }`
 * (common.css:22140) — so with no `.print-content` on the page, Print produces
 * BLANK paper. The React port called `window.print()` on the live dialog, which
 * is exactly that case: the reported "Print returns an empty file".
 *
 * Same shape as `CoilPrintSheet.tsx` (the coil sheet was ported first). The
 * stylesheet is `rating_mode_print.scss`, compiled into common.css:22275-22505
 * (`@media print { .fan-rating-mode … }`) plus the `.fan-rating-mode_print`
 * overrides at :23564-23634. It keys on `td:nth-of-type(N)` for the seven grid
 * columns, so the NUMBER and ORDER of `<td>`s in every row is load-bearing —
 * including the empty ones. Rows are transcribed 1:1 from the template; the
 * `tmpl:NNN` comments are the line each block came from.
 *
 * Formatting: legacy post-processes the mounted HTML in two passes
 * (correctPrintStyles:8360-8373) — `.decimal` cells get `Number(x).toFixed(2)`,
 * and a `.label` reading exactly "FEI" is retitled "Fan Efficiency Index" (a
 * no-op here: the template already spells it out). We format up-front instead,
 * which is the established port convention.
 */

import { useEffect, useRef } from 'react';
import { fanCmsOptionListByPath, type FanCmsDoc } from './fanCms';
import { fanMasterSelectedLabel } from './fanMasterMap';
import { drawFanCurves, type FanChosenCurve } from './FanCurveCanvas';

// ---------------------------------------------------------------------------
// leaf renderers — the template's four repeated span shapes
// ---------------------------------------------------------------------------

const Label = ({ children }: { children?: React.ReactNode }) => (
  <span className="label">{children}</span>
);

const Unit = ({ children }: { children?: React.ReactNode }) => (
  <span className="unit">{children}</span>
);

const Sel = ({ children }: { children?: React.ReactNode }) => (
  <span className="print-selection">{children}</span>
);

/** `.decimal` — correctPrintStyles:8360-8368 (`Number(text).toFixed(2)`, and a
 * blank stays blank because the loop skips falsy text). */
const decimal = (v: unknown): string => {
  // legacy reads the cell's TEXT, so an empty cell stays empty and "0" becomes
  // "0.00"; a non-numeric string would become NaN there, which we keep as-is.
  const text = v === null || v === undefined ? '' : String(v);
  if (!text) return '';
  const n = Number(text);
  return Number.isNaN(n) ? text : n.toFixed(2);
};

/** `get_one_decimal` (ahu/libs/handlebars/helpers.js:154-157). */
const getOneDecimal = (...args: unknown[]): string => {
  const v = args.find((el) => el && typeof el !== 'object');
  const res = Number(v);
  return res ? res.toFixed(2) : '';
};

/** `multiply` (helpers.js:141-148): null in → " " out, else the product to 2dp.
 * Same port as FanSelector's `hbMultiply`. */
const multiply = (...args: unknown[]): string => {
  if (args.some((a) => a === null || a === undefined)) return ' ';
  return args.reduce<number>((acc, a) => acc * Number(a), 1).toFixed(2);
};

const blank = (v: unknown): string =>
  v === null || v === undefined ? '' : String(v);

// ---------------------------------------------------------------------------

export interface FanPrintSheetProps {
  /** user.report_color — the raw legacy "(r,g,b)" string, used as `rgb${…}`. */
  reportColor?: string | null;
  /** project.report_co — the company line above the project block. */
  reportCo?: string | null;
  /** project.report_logo — legacy appends an <img> into `.mfg-img` (js:8356). */
  reportLogo?: string | null;
  year: number;
  /** `self.header` — the independent-rating popup host has no project/AHU. */
  headerMode?: boolean;

  projectName?: string | null;
  jobNumber?: string | null;
  quoteNumber?: string | null;
  elevation?: unknown;
  ahuTag?: string | null;
  sectionTag?: string | null;
  /** `self.rateing_time || model.rating_datetime`. */
  ratingDatetime?: string | null;

  /** the fans CMS params doc (legacy `cms.fan`). */
  fanDoc?: FanCmsDoc;
  /** the flat fan params + model state (FanSelector `state.current`). */
  s: Record<string, any>;
  /** `self.choosen` — the chosen rated row merged with its curve. */
  choosen: Record<string, any> | null;
  /** `self.selected` — the stored rated row the dialog opened on. */
  selected: Record<string, any> | null;
  /** `self.motor_choosen || self.selectedMotorData`. */
  motor: Record<string, any> | null;
  /** the dependency-graph master lists (guards / AFS / controller). */
  masterLists: Record<string, any[]>;
  masterFullLists: Record<string, any[]>;
  /** `self.notes.toJSON()` — the manufacturer notes. */
  notes: Array<{ note?: string }>;
  /** model.motor_power_total — the 37/67 branch of the motor Power total. */
  motorPowerTotal?: unknown;

  /** the curve to plot into the sheet's own `#curves` (js:8352-8353). */
  curve: FanChosenCurve | null;
  mfgId: number;
  airflowPerFan: number;
}

export const FanPrintSheet = (p: FanPrintSheetProps) => {
  const { s, choosen, selected, motor, fanDoc, headerMode } = p;

  // -- the template's four lookup shapes -------------------------------------
  const row = (path: string, id: unknown): Record<string, any> | undefined => {
    if (id == null || id === '') return undefined;
    return fanCmsOptionListByPath(fanDoc, path).find(
      (o: any) => String(o.id) == String(id)
    ) as Record<string, any> | undefined;
  };
  /** `{{#each …}}{{#ifCompare id '==' …}}<span>{{name}}</span>` */
  const cmsName = (path: string, id: unknown) => blank(row(path, id)?.name);
  /** the `{{value}}` lists (wheel percents / nominal diameter). */
  const cmsValue = (path: string, id: unknown) => blank(row(path, id)?.value);
  /** the `{{series}}` lists (fan series, motor series). */
  const cmsSeries = (path: string, id: unknown) => blank(row(path, id)?.series);
  /** a master-list select's option TEXT — legacy reads it straight off the
   * live `<option>` in correctPrintStyles (js:8314-8343). F3 fallback first,
   * exactly like the on-screen select (FanSelector's BareMasterSelect). */
  const master = (field: string, id: unknown) =>
    fanMasterSelectedLabel(field, id as any, p.masterFullLists[field]) ||
    fanMasterSelectedLabel(field, id as any, p.masterLists[field]);

  const mfg = Number(s.mfg_id);
  const motorTypeId = Number(s.motor_type_id);
  const powerUnit = motorTypeId == 1 ? 'hp' : 'KW';
  // tmpl:521 `fan_type_37_67_164` (js:8180-8183).
  const fanType376764 = mfg == 67 || mfg == 37 || mfg == 164;
  // js:8191 — `data.model.qty = qty_wide * qty_high`.
  const qty = Number(s.qty_wide) * Number(s.qty_high);

  // js:8186-8190 — the manufacturer NAME, matched on id AND the generic flag.
  const manufacturer = blank(
    (fanCmsOptionListByPath(fanDoc, 'fan.manufacturer') as any[]).find(
      (el: any) => el.id == s.mfg_id && !!el.generic_mfg == !!s.generic_mfg
    )?.name
  );

  // the selected fan TYPE's `value` — the Housing / Style rows key off it.
  const typeValue = blank(row('fan.type', s.type_id)?.value);

  // correctPrintStyles:8344-8349 — the two params the sheet patches before it
  // renders: wheel material falls back to the chosen row, and a blank/"--"
  // style falls back to the chosen row's own style_id.
  const wheelMtlId = s.wheel_mtl_id ?? choosen?.wheel_mtl_id ?? s.wheel_mtl;
  const styleId =
    !s.style_id || s.style_id === '--' ? choosen?.style_id : s.style_id;

  // js:8125-8135 — bhp/bhp-with-belt totals and the tsp/airflow rounding the
  // print context applies on its way in. calcBHPTotal (js:5551-5580) is
  // `bhp_per_fan * qty_wide * qty_high` to 2dp.
  const bhpTotal =
    choosen?.bhp != null && Number.isFinite(qty)
      ? (Number(choosen.bhp) * Number(s.qty_wide) * Number(s.qty_high)).toFixed(2)
      : undefined;
  const airflowPerFanRounded =
    s.airflow_per_fan == null || s.airflow_per_fan === ''
      ? ''
      : String(Math.round(Number(s.airflow_per_fan)));
  const tspDisplay =
    typeof s.tsp === 'string' ? s.tsp : s.tsp == null ? '' : Number(s.tsp).toFixed(2);

  // tmpl:213-243 — the Wheel Dia/Width cell reads the CMS ids off the STORED
  // row and the direct values off a fresh rate.
  const wheelDiaNominal = selected
    ? cmsValue('fan.wheel.nominal_diameter', selected.wheel_dia_nominal)
    : blank(choosen?.wheel_dia_nominal);
  const wheelDiaPercent = selected
    ? cmsValue('fan.wheel.dia_percent', selected.wheel_dia_percent_id)
    : blank(choosen?.wheel_dia_percent);
  const wheelWidthPercent = selected
    ? cmsValue('fan.wheel.width_percent', selected.wheel_width_percent_id)
    : blank(choosen?.wheel_width_percent);
  // tmpl:330-361 — same if/else for the AMCA class label and value.
  const amcaClass = selected
    ? cmsName('fan.amca_classes', selected.amca_class)
    : blank(choosen?.amca_class);

  // tmpl:426-435 — the inlet guard's MODEL name off the guard master list,
  // and (verbatim, quirk and all) the outlet guard's raw ID.
  const inletGuard = master('inlet_guard', s.inlet_guard);
  const outletGuardId = s.outlet_guard_id ?? s.outlet_guard;
  const protectiveCageName = master('protective_cage', s.protective_cage);
  const afsSensorName = master('fan_afs_sensor', s.fan_afs_sensor);
  const afsXmitterName = master('fan_afs_transmitter', s.fan_afs_transmitter);
  const mtrController = master('mtr_controller_id', s.mtr_controller_id);

  // ---- the sheet's own canvas (js:8352-8353 `drawCurves($('.print-content
  // #curves')[0])`). The print canvas is NOT inside `#fan-curve-section`, so
  // legacy's responsive-resize branch (js:7773) does not run for it and the
  // buffer stays at the element's intrinsic 300x150 — `autoSize: false` is
  // that guard.
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const cfmRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !p.curve || !p.curve.system_curve) return;
    drawFanCurves({
      canvas,
      choosen: p.curve,
      mfgId: p.mfgId,
      airflowPerFan: p.airflowPerFan,
      zoomed: false,
      canvasFont: '8px Arial',
      cfmMultiplierEl: cfmRef.current,
      autoSize: false,
    });
  }, [p.curve, p.mfgId, p.airflowPerFan]);

  const bandsIn = ['dbi_63', 'dbi_125', 'dbi_250', 'dbi_500', 'dbi_1000', 'dbi_2000', 'dbi_4000', 'dbi_8000', 'in_lwa'];
  const bandsOut = ['dbo_63', 'dbo_125', 'dbo_250', 'dbo_500', 'dbo_1000', 'dbo_2000', 'dbo_4000', 'dbo_8000', 'out_lwa'];

  return (
    <>
      {/* tmpl:8-9 — a SIBLING of `.fan-rating-mode`, not a child (it is
          `display:none !important` in print, common.css:23863). */}
      <header className="print-hide" />
      <div className="fan-rating-mode fan-rating-mode_print">
      {/* tmpl:11-26 — the running header. */}
      <table>
        <tbody>
          <tr>
            <td>
              <div
                className="container-header clearfix"
                style={{ borderTop: `4px solid rgb${p.reportColor ?? ''}` }}
              >
                <h3>
                  <span className="project-ahu-names">
                    <span className="report-name">Fan Selection</span>
                    <span className="project-name">
                      <span>{p.projectName ?? ''}</span>
                      {p.ahuTag ? (
                        <>
                          {' : '}
                          <span className="ahu-name">{p.ahuTag}</span>
                        </>
                      ) : null}
                    </span>
                  </span>
                  <span className="time-stamp">Rated on: {p.ratingDatetime ?? ''}</span>
                </h3>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* tmpl:27-72 — logo + project block. */}
      <table className="table-mfg-info">
        <tbody>
          <tr>
            <td>
              <div className="mfg-img">
                {/* js:8356-8358 — legacy APPENDS this img after compiling. */}
                {p.reportLogo ? (
                  <img src={p.reportLogo} tabIndex={-1} className="print-logo" alt="" />
                ) : null}
              </div>
            </td>
            <td>
              <div className="project-info">
                <span>{p.reportCo ?? ''}</span>
                {/* ★ JSX WHITESPACE. The template wraps these three values on
                    their own line —
                      <span>
                        {{#unless header}}{{project.name}}{{else}}…{{/unless}}
                      </span>
                    (tmpl:38-40 / :54-57 / :60-67) — so HTML collapses the
                    newlines to a leading AND trailing space and the value sits
                    one space-width right of the `.label` column. JSX drops
                    them, which measured as a 3px left shift on all three rows
                    against dev's sheet. The `{' '}`s put the spaces back. */}
                <ul>
                  <li>
                    <Label>Project Name</Label>
                    <span> {headerMode ? '' : (p.projectName ?? '')} </span>
                  </li>
                  {!headerMode && p.jobNumber ? (
                    <li>
                      <Label>Job No.</Label>
                      <span>{p.jobNumber}</span>
                    </li>
                  ) : null}
                  {!headerMode ? (
                    <li>
                      <Label>Project (Quote) No.</Label>
                      <span>{p.quoteNumber ?? ''}</span>
                    </li>
                  ) : null}
                  <li>
                    <Label>AHU</Label>
                    <span> {headerMode ? '' : (p.ahuTag ?? '')} </span>
                  </li>
                  <li>
                    <Label>Section</Label>
                    <span> {headerMode ? 'Fan' : (p.sectionTag || 'Fan')} </span>
                  </li>
                </ul>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* tmpl:73-662 — the seven-column grid. Every empty <td> is load-bearing:
          the widths come from `td:nth-of-type(N)` (common.css:22284-22366). */}
      <table className="tabel">
        <tbody>
          {/* tmpl:74-88 */}
          <tr>
            <td><Label>Manufacturer</Label></td>
            <td />
            <td><Sel><span>{manufacturer}</span></Sel></td>
            <td />
            <td />
            <td><Label>Per fan</Label></td>
            <td><Label>Total all fans</Label></td>
          </tr>
          {/* tmpl:89-112 */}
          <tr>
            <td><Label><span>Series</span></Label></td>
            <td />
            <td><Sel><span>{cmsSeries('fan.series', s.series_id)}</span></Sel></td>
            <td><Label>Airflow</Label></td>
            <td><Unit>CFM</Unit></td>
            <td><Sel>{airflowPerFanRounded}</Sel></td>
            <td><Sel>{blank(s.airflow_actual)}</Sel></td>
          </tr>
          {/* tmpl:113-136 */}
          <tr>
            <td><Label><span>Model</span></Label></td>
            <td />
            <td><Sel>{blank(choosen?.model ?? choosen?.fan_model)}</Sel></td>
            <td><Label><span>Air Stream</span></Label></td>
            <td />
            <td><Sel><span>{cmsName('fan.air_stream', s.air_stream_id)}</span></Sel></td>
          </tr>
          {/* tmpl:137-160 */}
          <tr>
            <td><Label><span>Type</span></Label></td>
            <td />
            <td><Sel><span>{cmsName('fan.type', s.type_id)}</span></Sel></td>
            <td><Label><span>Total SP</span></Label></td>
            <td><Unit>in WC</Unit></td>
            <td><Sel><span>{tspDisplay}</span></Sel></td>
          </tr>
          {/* tmpl:161-179 — Housing, cube fans only. */}
          <tr>
            <td><Label>{s.cube_housing_id ? <span>Housing</span> : null}</Label></td>
            <td />
            <td>
              {typeValue == 'cube' ? (
                <span>
                  <span>{cmsName('fan.cube_housing', s.cube_housing_id)}</span>
                </span>
              ) : null}
            </td>
            <td />
            <td />
            <td />
          </tr>
          {/* tmpl:180-205 — Style, plug fans only. */}
          <tr>
            <td>
              <Label>
                {styleId ? (
                  <span>
                    {typeValue == 'plug' && row('fan.style', styleId) ? (
                      <span>
                        <span>Style</span>
                      </span>
                    ) : null}
                  </span>
                ) : null}
              </Label>
            </td>
            <td />
            <td>
              {typeValue == 'plug' ? (
                <span>
                  <span>{cmsName('fan.style', styleId)}</span>
                </span>
              ) : null}
            </td>
            <td><span>Speed - Operating</span></td>
            <td><Unit>RPM</Unit></td>
            <td>{blank(choosen?.speed)}</td>
          </tr>
          {/* tmpl:206-254 */}
          <tr>
            <td><span>Wheel - Dia - Width</span></td>
            <td />
            <td>
              <Sel>
                {wheelDiaNominal ? `${wheelDiaNominal} - ` : ''}
                <Sel>{wheelDiaPercent}</Sel>
                {' - '}
                <Sel>{wheelWidthPercent} %</Sel>
              </Sel>
            </td>
            <td><span>Speed - Maximum</span></td>
            <td><Unit>RPM</Unit></td>
            <td>{blank(choosen?.speed_max)}</td>
          </tr>
          {/* tmpl:255-286 */}
          <tr>
            <td><Label>Wheel Material</Label></td>
            <td />
            <td><Sel><span>{cmsName('fan.wheel.material', wheelMtlId)}</span></Sel></td>
            <td><span>Power - Operating</span></td>
            <td><Unit>{powerUnit}</Unit></td>
            <td>{motorTypeId == 1 ? blank(choosen?.bhp) : blank(choosen?.bp_kw)}</td>
            <td>
              {motorTypeId == 1
                ? blank(bhpTotal ?? choosen?.bhp_total)
                : blank(choosen?.bp_kw_total)}
            </td>
          </tr>
          {/* tmpl:287-309 */}
          <tr>
            <td><Label>Rotation</Label></td>
            <td />
            <td><Sel><span><span>{cmsName('fan.rotation', s.rotation_id)}</span></span></Sel></td>
            <td><Label>Efficiency S / T</Label></td>
            <td><Unit>%</Unit></td>
            <td>
              <Sel>{blank(choosen?.static_efficiency)}</Sel>
              {' / '}
              <Sel>{blank(choosen?.total_efficiency)}</Sel>
            </td>
          </tr>
          {/* tmpl:310-328 */}
          <tr>
            <td><Label>Arrangement</Label></td>
            <td />
            <td><Sel><span><span>{cmsName('fan.arrangement', s.arr_id)}</span></span></Sel></td>
            <td><Label>Fan Efficiency Index</Label></td>
            <td />
            <td><span className="decimal">{decimal(choosen?.fei)}</span></td>
          </tr>
          {/* tmpl:329-374 — Air Velocity is blank for ebm-papst (37). */}
          <tr>
            <td><Label>{amcaClass ? 'Class' : null}</Label></td>
            <td />
            <td><Sel><span>{amcaClass}</span></Sel></td>
            <td><Label>Air Velocity</Label></td>
            {mfg == 37 ? (
              <>
                <td />
                <td />
              </>
            ) : (
              <>
                <td><span className={`unit ${blank(s.mfg_id)}`}>fpm</span></td>
                <td>
                  <span className={`print-selection ${blank(s.mfg_id)}`}>
                    {blank(choosen?.outlet_vel)}
                  </span>
                </td>
              </>
            )}
          </tr>
          {/* tmpl:375-407 */}
          <tr>
            <td><Label>Qty Wide x High</Label></td>
            <td />
            <td>
              <Sel>
                <span>{blank(s.qty_wide)}</span>
                {' x '}
                <span>{blank(s.qty_high)}</span>
              </Sel>
            </td>
            <td><Label><span>Redundancy</span></Label></td>
            <td />
            <td>
              {choosen?.redundancy
                ? String(choosen.redundancy)
                : s.redundancy_id
                  ? <span>{cmsName('fan.redundancy', s.redundancy_id)}</span>
                  : 'None'}
            </td>
            <td />
          </tr>
          {/* tmpl:408-422 */}
          <tr>
            <td />
            <td />
            <td />
            <td><Label>Altitude</Label></td>
            <td><Unit>ft</Unit></td>
            <td><Sel>{blank(p.elevation)}</Sel></td>
            <td />
          </tr>
          {/* tmpl:423-449 */}
          <tr>
            <td>
              <Label>
                {inletGuard ? <span>Guard Inlet</span> : null}
                {outletGuardId ? ' / Outlet' : null}
              </Label>
            </td>
            <td />
            <td>
              <Sel>
                {inletGuard ? <span>{inletGuard}</span> : null}
                {outletGuardId ? ` / ${outletGuardId}` : null}
              </Sel>
            </td>
            <td><Label><span>Inlet Pressure</span></Label></td>
            <td><Unit>in WC</Unit></td>
            <td>
              <Sel>
                <Sel>{blank(s.fan_inlet_pressure)}</Sel>
              </Sel>
            </td>
          </tr>
          {/* tmpl:450-471 */}
          <tr>
            <td><Label>{protectiveCageName ? 'Protective Cage' : null}</Label></td>
            <td />
            <td><Sel>{protectiveCageName}</Sel></td>
            <td><Label><span>Temperature Inlet</span></Label></td>
            <td><span className="unit temp">F</span></td>
            <td>
              <Sel>
                <span className="decimal">{decimal(s.fan_inlet_temp)}</span>
              </Sel>
            </td>
          </tr>
          {/* tmpl:472-484 — the "hide"/"hide-"/"hide--" class trick (only the
              bare `hide` is a real rule, so the label shows as soon as either
              AFS name is set). */}
          <tr>
            <td>
              <span
                className={`label hide${afsSensorName ? '-' : ''}${afsXmitterName ? '-' : ''}`}
              >
                Airflow Station
              </span>
            </td>
            <td />
            <td>
              <Sel>
                {afsSensorName}
                {afsXmitterName ? ` / ${afsXmitterName}` : null}
              </Sel>
            </td>
            <td><Label>Inlet Density</Label></td>
            <td>
              <Unit>
                lb/ft<span className="sup">3</span>
              </Unit>
            </td>
            <td><Sel>{blank(choosen?.density)}</Sel></td>
          </tr>
          {/* tmpl:485-493 */}
          <tr>
            <td />
            <td />
            <td />
            <td />
            <td />
            <td />
          </tr>
          <tr className="empty" />
          {/* tmpl:494-498 */}
          <tr>
            <td><Label>Motor</Label></td>
          </tr>
          {/* tmpl:499-548 */}
          <tr>
            <td><Label>Manufacturer</Label></td>
            <td />
            <td><Sel><span>{cmsName('fan.motor.manufacturer', s.motor_mfg_id)}</span></Sel></td>
            <td><Label><span>Power</span></Label></td>
            <td><Unit>{powerUnit}</Unit></td>
            <td>
              <Sel>
                <span>
                  {fanType376764
                    ? getOneDecimal(s.motor_power)
                    : motorTypeId == 2
                      ? multiply(motor?.power, 0.7457)
                      : multiply(motor?.power)}
                </span>
              </Sel>
            </td>
            <td>
              <Sel>
                <span>
                  {mfg == 37 || mfg == 67
                    ? getOneDecimal(p.motorPowerTotal)
                    : motorTypeId == 2
                      ? multiply(motor?.power, qty, 0.7457)
                      : multiply(motor?.power, qty)}
                </span>
              </Sel>
            </td>
          </tr>
          {/* tmpl:549-575 */}
          <tr>
            <td><Label>Series</Label></td>
            <td />
            <td><Sel><span>{cmsSeries('fan.motor.series', s.motor_series_id)}</span></Sel></td>
            <td><Label><span>Voltage</span></Label></td>
            <td />
            <td>
              <Sel>
                {choosen?.motor_voltage
                  ? String(choosen.motor_voltage)
                  : <span>{cmsName('fan.motor.voltage', s.motor_voltage_id)}</span>}
              </Sel>
            </td>
          </tr>
          {/* tmpl:576-601 */}
          <tr>
            <td><Label>Model</Label></td>
            <td />
            <td><Sel>{blank(s.motor_model ?? motor?.model)}</Sel></td>
            <td><Label><span>Controller</span></Label></td>
            <td />
            <td>
              <Sel>
                <Sel>{mtrController}</Sel>
              </Sel>
            </td>
          </tr>
          {/* tmpl:602-616 */}
          <tr>
            <td><Label>Type</Label></td>
            <td />
            <td><Sel><span>{cmsName('fan.motor.type', s.motor_type_id)}</span></Sel></td>
            <td />
            <td />
            <td />
          </tr>
          {/* tmpl:617-652 — `tr.hide` is `display:none` in print
              (common.css:22294); transcribed for shape, never seen. */}
          <tr className="hide">
            {blank(row('fan.arrangement', s.arr_id)?.value) == '4' ? (
              <>
                <td><Label>Controller</Label></td>
                <td />
                <td>
                  {mfg == 37 ? (
                    <Sel><span>ECM</span></Sel>
                  ) : (
                    <Sel>
                      <span>{cmsName('fan.capacity_controller', s.mtr_controller_id)}</span>
                    </Sel>
                  )}
                </td>
              </>
            ) : (
              <>
                <td />
                <td />
                <td />
              </>
            )}
            <td><Label>Inlet Density</Label></td>
            <td>
              <Unit>
                lb/ft<span className="sup">3</span>
              </Unit>
            </td>
            <td><Sel>{blank(choosen?.density)}</Sel></td>
          </tr>
          {/* tmpl:653-661 */}
          <tr>
            <td><Label /></td>
            <td />
          </tr>
        </tbody>
      </table>

      <hr />

      {/* tmpl:664-738 — sound power bands + the curve. */}
      <table className="curve">
        <tbody>
          <tr>
            <td>
              {/* ★ The three `<ul>`s are `display:inline-block` (common.css:
                  22469) — they lay out as the three COLUMNS of the band table —
                  so the newlines the template has between them are real
                  whitespace text nodes that JSX would drop. The `{' '}`s put
                  them back; without them the columns sit 3-4px tighter than
                  dev's. (The `<li>`s inside stay `display:list-item`, so the
                  whitespace between THOSE is immaterial.) */}
              <div className="fan-list">
                <span>Sound Power Levels (dB all fans combined)</span>{' '}
                <ul className="rate-list-heading">
                  <li className="long">Band</li>
                  {['1', '2', '3', '4', '5', '6', '7', '8', 'LwA'].map((h) => (
                    <li className="medium" key={h}>{h}</li>
                  ))}
                </ul>{' '}
                <ul className="rate-list-heading">
                  <li className="long long_in">Inlet</li>
                  {choosen
                    ? bandsIn.map((k) => (
                        <li className="medium medium_in" key={k}>{blank(choosen[k])}</li>
                      ))
                    : null}
                </ul>{' '}
                <ul className="rate-list-heading">
                  <li className="long long_out">Outlet</li>
                  {choosen
                    ? bandsOut.map((k) => (
                        <li className="medium medium_out" key={k}>{blank(choosen[k])}</li>
                      ))
                    : null}
                </ul>
              </div>
            </td>
            <td>
              <div className="fan-curve">
                <div className={`fan-curve-center${choosen ? '' : ' no-curves'}`}>
                  <canvas id="curves" ref={canvasRef} />
                </div>
                <span className="fan-curve-top-wrapper">
                  <span>Standard Plot with SC (total all fans)</span>
                </span>
                <span className="fan-curve-right">Break Power (hp)</span>
                <span className="fan-curve-bottom">
                  Airflow (CFM<span className="cfm_multiplier" ref={cfmRef} />)
                </span>
                <span className="fan-curve-left">Static Pressure (in WC)</span>
              </div>
            </td>
          </tr>
          <tr className="notes">
            <td>
              {p.notes.map((n, i) => (
                <span key={i}>{n?.note ?? ''}</span>
              ))}
            </td>
          </tr>
          <tr>
            <td>
              <span />
            </td>
          </tr>
        </tbody>
      </table>

      {/* tmpl:739-746 */}
      <table className="table-footer">
        <tbody>
          <tr className="print-footer">
            <td>
              <h5 style={{ borderTop: `4px solid rgb${p.reportColor ?? ''}` }}>
                Powered by Kinetix Air | © {p.year} Kinetix Software LLC | All Rights
                Reserved
              </h5>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </>
  );
};

export default FanPrintSheet;
